import moment from 'moment-timezone'

import { getIdOrNull, requestMultipart, requestWithPromise } from '..'
import { HttpMethods, makeQueryString } from '../apiHelper'
import { ImportModes, LanguageTags } from '../constants'
import apiClient from './apiClient'
import { StudentExtraInfo } from '../entities/studentExtraInfo' // eslint-disable-line no-unused-vars

export const Genders = Object.freeze({
  MALE: 'M',
  FEMALE: 'F',
  OTHER: 'Other'
})

export const SerializationContexts = Object.freeze({
  USER: 'user',
  NISS: 'niss',
  SCHOOL: 'school',
  SECTION: 'section',
  STATS: 'stats',
  /** Badge assignations of the current institution that are valid today. */
  BADGE_ASSIGNATIONS: 'badge-assignations'
})

export const getStudentInternships = (user, student, parameters = {}) => {
  return requestWithPromise(
    `/api/students/${getIdOrNull(student)}/internships${makeQueryString(parameters)}`,
    HttpMethods.GET,
    null,
    user
  )
}

export const getStudentById = (user, student) => {
  return requestWithPromise(
    `/api/students/${getIdOrNull(student)}`,
    HttpMethods.GET,
    null,
    user
  )
}

export const getStudentExtraInformations = (user, student, parameters = {}) => {
  return requestWithPromise(
    `/api/students/${getIdOrNull(student)}/extra-contact-informations${makeQueryString(parameters)}`,
    HttpMethods.GET,
    null,
    user
  )
}

/** @param {StudentExtraInfo} studentExtraInformation */
export const createStudentExtraInformations = (student, user, studentExtraInformation) => {
  return requestWithPromise(
    `/api/students/${getIdOrNull(student)}/extra-contact-informations`,
    HttpMethods.POST,
    studentExtraInformation.toRequestBody(),
    user
  )
}

/** @param {StudentExtraInfo} studentExtraInformation */
export const updateStudentExtraInformations = (student, user, studentExtraInformation) => {
  return requestWithPromise(
    `/api/students/${getIdOrNull(student)}/extra-contact-informations/${getIdOrNull(studentExtraInformation)}`,
    HttpMethods.PATCH,
    studentExtraInformation.toRequestBody(),
    user
  )
}

export const deleteStudentExtraInformations = (student, user, studentExtraInformation) => {
  return requestWithPromise(
    `/api/students/${getIdOrNull(student)}/extra-contact-informations/${getIdOrNull(studentExtraInformation)}`,
    HttpMethods.DELETE,
    null,
    user
  )
}

export const getStudentOptins = (student, user, parameters = {}) => {
  return requestWithPromise(
    `/api/students/${getIdOrNull(student)}/optins${makeQueryString(parameters)}`,
    HttpMethods.GET,
    null,
    user
  )
}

export const getAllStudents = (user, parameters = {}) => apiClient.get(user, 'api/students', parameters)

export const getStudentBadges = (student, user) => {
  return requestWithPromise(
    `/api/students/${getIdOrNull(student)}/badges`,
    HttpMethods.GET,
    null,
    user
  )
}

export const getStudentFreeFields = (student, user, parameters = {}) => {
  return requestWithPromise(
    `/api/students/${getIdOrNull(student)}/free-fields${makeQueryString(parameters)}`,
    HttpMethods.GET,
    null,
    user
  )
}

export const getStudentSchoolOptions = (user, student) => apiClient.get(user, `student/${getIdOrNull(student)}/school-options`)

const DEFAULT_PARAM_EXPORT_STUDENT_INTERNSHIP_HISTORY = { language: LanguageTags.FR, timezone: moment.tz.guess() }
export const exportStudentInternshipsHistory = (user, student, body = DEFAULT_PARAM_EXPORT_STUDENT_INTERNSHIP_HISTORY) => {
  return requestWithPromise(
    `/api/students/${getIdOrNull(student)}/export-internships-history`,
    HttpMethods.POST,
    body,
    user
  )
}

export const updateNiss = (user, student, niss) => {
  return requestWithPromise(
    `/api/student/update-niss/${getIdOrNull(student)}`,
    HttpMethods.PATCH,
    { niss },
    user
  )
}

export const updateStudent = (user, student, parameters) => apiClient.patch(user, `api/students/${getIdOrNull(student)}`, parameters)

export const getStudentEvaluations = (user, student) => {
  return requestWithPromise(
    `/evaluation/student/${getIdOrNull(student)}`,
    HttpMethods.GET,
    null,
    user
  )
}

export const getRedirectAction = email => apiClient.get(null, `student/redirect/${email}`)
export const createStudent = (user, body) => apiClient.post(user, 'api/students', body)
export const deleteStudent = (user, student) => apiClient.delete(user, `api/students/${getIdOrNull(student)}`)
export const exportStudents = (user, body) => apiClient.post(user, 'export/students', body)

export const getStudentBadgeAssignations = (user, student) => apiClient.get(user, `api/students/${getIdOrNull(student)}/badge-assignations`)

/**
 * Uploads a new profile picture for a student. The file name is built backend side from the
 * student's id, firstname and lastname, hence their presence in the multipart body.
 *
 * @param {object} user    the authenticated user
 * @param {object} student the student the picture belongs to
 * @param {Blob}   file    the picture to upload
 */
export const uploadStudentProfileImage = (user, student, file) => {
  return requestMultipart(
    '/profil/image',
    HttpMethods.POST,
    {
      file,
      id: getIdOrNull(student),
      firstname: student.firstname,
      lastname: student.lastname
    },
    user
  )
}

export const importStudents = (user, file, mode = ImportModes.DRY_RUN) => {
  return requestMultipart('/import-students', HttpMethods.POST, { file, mode }, user).then(response => {
    if (!response || response.status === 'error' || !Array.isArray(response.data)) {
      return Promise.reject(response || new Error('UNEXPECTED'))
    }

    return response
  })
}
